import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams, TableQueryState } from '@/components/DataTable';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import type { ProjectDataTableItem } from '@kirin/api-client/contracts/projects';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { createProjectColumns } from './columns';
import { fetchProjectsDataTable } from './api';
import { PROJECTS_TABLE_CONFIG } from './constants';
import { ProjectUserListModal } from '../../components/ProjectUserListModal';
import { ProjectDrawer } from './ProjectDrawer';

// The ticket's default sort: most missing + partial security instructions first.
const DEFAULT_SORT: TableQueryState['sort'] = {
  field: 'coverageGaps',
  order: SortOrder.Desc,
};
// Fallback default when the Policy coverage column is flag-hidden — sorting by a
// column the user can't see is confusing, so fall back to most-recently-seen.
const FALLBACK_SORT: TableQueryState['sort'] = {
  field: 'lastSeenAt',
  order: SortOrder.Desc,
};

interface DataTableProjectsContentProps {
  organizationId: string;
}

export function DataTableProjectsContent({
  organizationId,
}: DataTableProjectsContentProps): ReactElement {
  const [selectedProject, setSelectedProject] =
    useState<ProjectDataTableItem | null>(null);

  // Policy coverage rides the AI instruction catalog flag; Supply chain rides
  // the packages flag (agent_command_visibility) — each column's data source.
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG,
    flagsmithFeatureFlags.AGENT_COMMAND_VISIBILITY,
  ]);
  const showPolicyCoverage =
    flags[flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG] ?? false;
  const showSupplyChain =
    flags[flagsmithFeatureFlags.AGENT_COMMAND_VISIBILITY] ?? false;

  const columns = useMemo(
    () =>
      createProjectColumns(organizationId, setSelectedProject, {
        showPolicyCoverage,
        showSupplyChain,
      }),
    [organizationId, showPolicyCoverage, showSupplyChain]
  );

  const renderDrawer = useCallback(
    (row: ProjectDataTableItem) => (
      <ProjectDrawer
        row={row}
        organizationId={organizationId}
        showPolicyCoverage={showPolicyCoverage}
        showSupplyChain={showSupplyChain}
      />
    ),
    [organizationId, showPolicyCoverage, showSupplyChain]
  );

  // Default to the coverage-gaps sort only when that column is visible; else it
  // would sort by a hidden metric.
  const initialState = useMemo(
    () => ({ sort: showPolicyCoverage ? DEFAULT_SORT : FALLBACK_SORT }),
    [showPolicyCoverage]
  );

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) =>
      queryKeys.projects.dataTable(organizationId, {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        filters: params.filters,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
        search: params.search,
      }),
    [organizationId]
  );

  const queryConfig = useMemo(
    () => ({ staleTime: 30000, refetchOnWindowFocus: false }),
    []
  );

  const searchConfig = useMemo(
    () => ({ placeholder: 'Search projects...', className: 'w-[260px]' }),
    []
  );

  return (
    <>
      <DataTable<ProjectDataTableItem>
        id={PROJECTS_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the projects table"
        search={searchConfig}
        features={{ enableSorting: true, enableRowExpansion: true }}
        initialState={initialState}
        rowExpansion={{
          render: renderDrawer,
          getRowId: row => row.slug,
          // The name cell is a navigation link; only the chevron toggles the
          // drawer so a row click doesn't fight the link.
          expandOnRowClick: false,
          showContainer: false,
          contentClassName: '',
        }}
        queryFn={(params, signal) =>
          fetchProjectsDataTable(organizationId, params, signal)
        }
        getQueryKeysFromParams={getQueryKeysFromParams}
        noDataLabel="No projects found."
        queryConfig={queryConfig}
      />
      <ProjectUserListModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </>
  );
}

DataTableProjectsContent.displayName = 'DataTableProjectsContent';
